import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { Container, Row, Col } from 'styled-bootstrap-grid';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MdAccessTime, MdLink } from 'react-icons/md';
import api from '../../../services/api';

import { useToast } from '../../../hooks/toast';

import getValidationErrors from '../../../utils/getValidationErrors';

import Header from '../../../containers/Header';
import Footer from '../../../containers/Footer';
import Breadcrumbs from '../../../containers/Breadcrumbs';

import Card from '../../../components/Card';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import Loader from '../../../components/Loader';
import Dropzone from '../../../components/Dropzone';
import Select from '../../../components/Select';
import CreatableSelect from '../../../components/CreatableSelect';

import { ThemeDescription, DataBox, DataList } from './styles';

interface ThemeData {
  created_at: string;
  name: string;
  thumbnail_url: string;
  material_url: string;
}

interface ThemePreviewFormData {
  name: string;
  difficulty: 'easy' | 'medium' | 'hard';
  category: string;
  exam_name: string;
}

const ThemePreview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const formRef = useRef<FormHandles>(null);

  const [theme, setTheme] = useState<ThemeData>({} as ThemeData);
  const [exams, setExams] = useState([]);
  const [themeCategories, setThemeCategories] = useState([]);

  const [themeLoading, setThemeLoading] = useState(false);
  const [statisticsLoading, setStatisticsLoading] = useState(false);

  const [materialFile, setMaterialFile] = useState<Blob | string>('');
  const [thumbnailFile, setThumbnailFile] = useState<Blob | string>('');

  const { addToast } = useToast();

  useEffect(() => {
    async function loadData() {
      setThemeLoading(true);

      try {
        const [
          examsResponse,
          categoriesResponse,
          themeResponse,
        ] = await Promise.all([
          api.get('/exams/list'),
          api.get('/themes/categories/list'),
          api.get(`/themes/${id}`),
        ]);

        setExams(examsResponse.data);
        setThemeCategories(categoriesResponse.data);

        const created_at = format(
          parseISO(themeResponse.data.created_at),
          "dd/MM/yyyy 'às' HH:mm",
          { locale: ptBR },
        );

        setTheme({ ...themeResponse.data, created_at });

        formRef.current?.setData({
          category: themeResponse.data.category.name,
          difficulty: themeResponse.data.difficulty,
          exam_name: themeResponse.data.exam_name,
        });

        setThemeLoading(false);
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Ocorreu um erro',
          description: 'O tema não foi encontrado!',
        });

        history.push('/temas');
      }
    }

    loadData();
  }, [id, addToast, history]);

  const handleSubmit = useCallback(
    async (data: ThemePreviewFormData) => {
      setThemeLoading(true);

      formRef.current?.setErrors({});

      try {
        const schema = Yup.object().shape({
          name: Yup.string().required('Nome obrigatório'),
          difficulty: Yup.string().required('Dificuldade obrigatória'),
          category: Yup.string().required('Categoria obrigatória'),
          exam_name: Yup.string().required('Exame obrigatório'),
        });

        await schema.validate(data, { abortEarly: false });

        const formData = new FormData();

        formData.append('name', data.name);
        formData.append('difficulty', data.difficulty);
        formData.append('category', data.category);
        formData.append('exam_name', data.exam_name);

        if (materialFile) formData.append('material', materialFile);
        if (thumbnailFile) formData.append('thumbnail', thumbnailFile);

        const updatedThemeResponse = await api.put<ThemeData>(
          `themes/${id}`,
          formData,
        );

        setTheme(updatedThemeResponse.data);

        addToast({
          type: 'success',
          title: 'Sucesso na atualização',
          description: 'O tema foi atualizado com sucesso!',
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          return;
        }

        addToast({
          type: 'error',
          title: 'Erro na atualização',
          description: 'Ocorreu um erro na atualização, verifique os campos.',
        });
      } finally {
        setThemeLoading(false);
      }
    },
    [addToast, id, materialFile, thumbnailFile],
  );

  return (
    <>
      <Header />

      <Container style={{ flex: 1 }}>
        <Breadcrumbs
          title="Visualizar tema"
          items={[
            { title: 'Temas', link: '/temas' },
            { title: 'Visualizar tema' },
          ]}
        />

        <Row>
          <Col md={4}>
            <Card>
              {themeLoading && <Loader />}

              <ThemeDescription>
                <img src={theme.thumbnail_url} alt={theme.name} />

                <h2>{theme.name}</h2>

                <div>
                  <span>
                    <MdAccessTime size={16} />
                    {theme.created_at}
                  </span>

                  <span>
                    <MdLink size={16} />
                    <a href={theme.material_url} target="blank">
                      Link do material
                    </a>
                  </span>
                </div>
              </ThemeDescription>
            </Card>

            <Row>
              <Col md={12}>
                <DataBox>
                  {statisticsLoading && <Loader />}
                  Redações enviadas
                  <span>?</span>
                </DataBox>
              </Col>

              <Col md={12}>
                <DataBox>
                  {statisticsLoading && <Loader />}
                  Média de notas
                  <span>?</span>
                </DataBox>
              </Col>
            </Row>
          </Col>

          <Col md={8}>
            <Card>
              {themeLoading && <Loader />}

              <Form
                ref={formRef}
                initialData={theme}
                onSubmit={handleSubmit}
                noValidate
              >
                <Row>
                  <Col md={12}>
                    <Input type="text" name="name" label="Nome" />
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <CreatableSelect
                      name="category"
                      label="Categoria"
                      options={themeCategories}
                    />
                  </Col>

                  <Col md={4}>
                    <CreatableSelect
                      name="difficulty"
                      label="Dificuldade"
                      options={[
                        {
                          label: 'Fácil',
                          value: 'easy',
                        },
                        {
                          label: 'Médio',
                          value: 'medium',
                        },
                        {
                          label: 'Difícil',
                          value: 'hard',
                        },
                      ]}
                    />
                  </Col>

                  <Col md={4}>
                    <Select name="exam_name" label="Exame" options={exams} />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Dropzone
                      label="Material"
                      accept=".pdf"
                      maxSize={300000}
                      onDrop={acceptedFile => setMaterialFile(acceptedFile)}
                    />
                  </Col>

                  <Col md={6}>
                    <Dropzone
                      label="Thumbnail"
                      accept="image/*"
                      maxSize={300000}
                      onDrop={acceptedFile => setThumbnailFile(acceptedFile)}
                    />
                  </Col>
                </Row>

                <Button
                  type="submit"
                  color="primary"
                  style={{ marginTop: '16px' }}
                >
                  Atualizar tema
                </Button>
              </Form>
            </Card>

            <Card>
              {statisticsLoading && <Loader />}
              <h2>Erros mais cometidos</h2>
              <DataList>
                <li>
                  Erro ortográfico
                  <span>?</span>
                </li>
                <li>
                  Palavra usada desnecessáriamente
                  <span>?</span>
                </li>
                <li>
                  Tangenciamento do Tema
                  <span>?</span>
                </li>
                <li>
                  Argumento desnecessário
                  <span>?</span>
                </li>
                <li>
                  Desfecho precário
                  <span>?</span>
                </li>
              </DataList>
            </Card>
          </Col>
        </Row>
      </Container>

      <Footer />
    </>
  );
};

export default ThemePreview;
